import { DRAFT_BEGIN_MARKER, DRAFT_END_MARKER, FACTS, SECTIONS, SectionMeta, SectionStatus } from './prototype1Data';
import type { ChatRequest } from '../_shared/chatService';
import type { ScreenId, ChatMessage } from './Prototype1Context';

type ChatIntent = 'draft' | 'edit-current-draft' | 'expand' | 'qa' | 'propose-chart' | 'review';

type PanelState = 'chat-open' | 'chat-closed' | 'fullscreen-editor';

interface ResponseHint {
  shape: 'chat-reply' | 'draft-paragraph' | 'rewrite-paragraph';
  max_words?: number;
}

interface ScreenContextSlice {
  intent: ChatIntent;
  active_section_id?: SectionMeta['id'];
  current_draft?: string;
  ui_state?: { panel?: PanelState; last_user_action?: string };
  response_hint?: ResponseHint;
}

const SCREEN_INDEX: Record<ScreenId, number> = {
  '01-dashboard': 1,
  '02-college-eligibility-not-started': 2,
  '03-equity-gaps-drafting': 3,
  '04-chat-panel-closed': 4,
  '05-dashboard-report-overview': 5,
  '06-section-editor-ai-draft': 6,
  '07-dashboard-all-sections-complete': 7,
  '08-full-report-review': 8,
};

const MAX_CONTEXT_LENGTH = 4000;
const TRUNCATION_MARKER = '… [truncated]';

interface ResponseProtocol {
  surfaces: {
    chat_panel: string;
    section_editor: string;
  };
  actions: {
    draft: { begin: string; end: string; notes: string };
    approve: { marker: string; notes: string };
  };
  rules: string[];
}

interface ContextObject {
  prototype: { id: 1; name: 'district-ai-tool' };
  screen: { id: ScreenId; index: number; total: number };
  intent: ChatIntent;
  district: typeof FACTS.district;
  report: {
    sections: Array<{
      id: string;
      title: string;
      index: number;
      status: SectionStatus;
      description: string;
    }>;
    progress: { complete: number; total: number; percent: number };
  };
  active_section?: {
    id: string;
    title: string;
    index: number;
    status: SectionStatus;
    current_draft?: string;
  };
  facts: typeof FACTS;
  ui_state: { panel?: PanelState; last_user_action?: string };
  response_hint?: ResponseHint;
  response_protocol: ResponseProtocol;
  prior_assistant_opening?: string;
}

const RESPONSE_PROTOCOL: ResponseProtocol = {
  surfaces: {
    chat_panel: 'right-side chat; conversational reply, questions, acknowledgements',
    section_editor: 'left-side section editor; canonical section markdown the user reviews and approves',
  },
  actions: {
    draft: {
      begin: `${DRAFT_BEGIN_MARKER} (or <<<DRAFT_BEGIN:section-id>>>)`,
      end: DRAFT_END_MARKER,
      notes:
        'Emit only when this turn produces new/revised section content. Body must be canonical section prose in GitHub-flavoured markdown — no follow-up questions, no acknowledgements, no chat-style language.',
    },
    approve: {
      marker: '<<<APPROVE>>> or <<<APPROVE:section-id>>>',
      notes:
        'Emit when the user accepts a section as done/looks-good/approved. Defaults to the active section when no id is supplied.',
    },
  },
  rules: [
    'Markers may appear in any order. Everything outside markers is the chat reply.',
    'Never emit empty marker pairs or markers that do not match the user intent.',
    'Never restate or echo the existing draft body outside the DRAFT markers.',
  ],
};

const serializeContext = (ctx: ContextObject): string => {
  let serialized = JSON.stringify(ctx);
  if (serialized.length <= MAX_CONTEXT_LENGTH) return serialized;

  if (ctx.active_section?.current_draft) {
    const allowance = MAX_CONTEXT_LENGTH - (serialized.length - ctx.active_section.current_draft.length) - 200;
    const cutoff = Math.max(200, allowance);
    ctx.active_section.current_draft = ctx.active_section.current_draft.slice(0, cutoff) + TRUNCATION_MARKER;
    serialized = JSON.stringify(ctx);
  }

  if (serialized.length > MAX_CONTEXT_LENGTH) {
    serialized = serialized.slice(0, MAX_CONTEXT_LENGTH - TRUNCATION_MARKER.length) + TRUNCATION_MARKER;
  }
  return serialized;
};

interface BuildChatRequestArgs {
  message: string;
  screenId: ScreenId;
  slice: ScreenContextSlice;
  conversation: ChatMessage[];
  conversationId?: string;
  scope: string;
  sectionStatuses: Record<string, SectionStatus>;
  eoaId: string;
}

interface SanitizedConversation {
  opening: string;
}

const SYNTHETIC_FALLBACK_CONTENT = '(chat backend unavailable — this is a prototype)';

const isSynthetic = (msg: ChatMessage): boolean => msg.synthetic === true || msg.content === SYNTHETIC_FALLBACK_CONTENT;

const sanitizeConversation = (conversation: ChatMessage[]): SanitizedConversation => {
  const dropped: ChatMessage[] = [];
  let i = 0;
  while (i < conversation.length) {
    const m = conversation[i];
    if (m.role === 'user') {
      const next = conversation[i + 1];
      if (next && next.role === 'assistant' && isSynthetic(next)) {
        i += 2;
        continue;
      }
      dropped.push(m);
      i += 1;
      continue;
    }
    if (isSynthetic(m)) {
      i += 1;
      continue;
    }
    dropped.push(m);
    i += 1;
  }

  let leadIdx = 0;
  while (leadIdx < dropped.length && dropped[leadIdx].role === 'assistant') {
    leadIdx += 1;
  }
  const opening = dropped
    .slice(0, leadIdx)
    .map((m) => m.content)
    .join('\n\n');

  return { opening };
};

const buildChatRequest = ({
  message,
  screenId,
  slice,
  conversation,
  conversationId,
  scope,
  sectionStatuses,
  eoaId,
}: BuildChatRequestArgs): ChatRequest => {
  const completeCount = SECTIONS.filter((s) => (sectionStatuses[s.id] ?? s.status) === 'complete').length;
  const total = SECTIONS.length;

  const activeSection = slice.active_section_id ? SECTIONS.find((s) => s.id === slice.active_section_id) : undefined;

  const contextObj: ContextObject = {
    prototype: { id: 1, name: 'district-ai-tool' },
    screen: { id: screenId, index: SCREEN_INDEX[screenId], total: 8 },
    intent: slice.intent,
    district: FACTS.district,
    report: {
      sections: SECTIONS.map(({ id, title, index, status, description }) => ({
        id,
        title,
        index,
        status: sectionStatuses[id] ?? status,
        description,
      })),
      progress: {
        complete: completeCount,
        total,
        percent: Math.round((completeCount / total) * 100),
      },
    },
    active_section: activeSection
      ? {
          id: activeSection.id,
          title: activeSection.title,
          index: activeSection.index,
          status: sectionStatuses[activeSection.id] ?? activeSection.status,
          current_draft: slice.current_draft,
        }
      : undefined,
    facts: FACTS,
    ui_state: slice.ui_state ?? {},
    response_hint: slice.response_hint,
    response_protocol: RESPONSE_PROTOCOL,
  };

  const { opening } = sanitizeConversation(conversation);
  if (opening) {
    contextObj.prior_assistant_opening = opening;
  }

  return {
    prompt: message,
    eoa_id: eoaId,
    scope,
    context: serializeContext(contextObj),
    conversation_id: conversationId,
    mode: 'mock',
  };
};

export { buildChatRequest };
export type { ChatIntent, ScreenContextSlice, ResponseHint, PanelState };
